import React, { useMemo } from 'react';
import { Card } from '../../components/ui/card';
import {
  BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid,
} from 'recharts';
import { Leaf, Car, Trees } from 'lucide-react';
import { emissionsFromUsageTotals, monthlyCo2eKg } from '../../lib/emissions';

const fmt = (n, d = 0) => Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: d, minimumFractionDigits: d });

function monthLabel(m) {
  if (!m || m.length < 7) return m;
  const [y, mo] = m.split('-');
  return new Date(Number(y), Number(mo) - 1, 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' }).replace(' ', " '");
}

/**
 * 4th dashboard card — Carbon Footprint. Computes CO2e from the same usage
 * rollups that drive the Electricity / Gas / Water tiles (EPA factors).
 */
export function SustainabilityCard({ usage }) {
  const em = useMemo(
    () => emissionsFromUsageTotals({
      electricity_kwh: usage?.electricity_kwh,
      gas_therms: usage?.gas_therms,
      water_gallons: usage?.water_gallons,
    }),
    [usage],
  );
  const series = useMemo(
    () => (usage?.series || []).map((s) => ({ month: s.month, co2e: +(monthlyCo2eKg(s) / 1000).toFixed(3) })),
    [usage],
  );

  const totalKg = em.total_kg;
  const showT = totalKg >= 1000;
  const val = showT ? fmt(em.total_mt, 2) : fmt(totalKg, 0);
  const unit = showT ? 't CO₂e' : 'kg CO₂e';
  const eq = em.equivalencies;

  return (
    <div>
      <Card className="overflow-hidden border-0 rounded-b-none" data-testid="commodity-carbon">
        <div className="bg-gradient-to-br from-emerald-500 via-green-500 to-teal-600 relative px-6 py-7 text-white">
          <div className="text-lg font-medium opacity-95">Carbon Footprint</div>
          <div className="mt-3 flex items-baseline gap-3">
            <span className="opacity-80"><Leaf className="h-7 w-7" /></span>
            <span className="text-4xl font-bold tracking-tight">{val}</span>
            <span className="text-base font-medium opacity-90">{unit}</span>
          </div>
        </div>
      </Card>

      <div className="border-x border-b bg-white" data-testid="carbon-chart">
        <div className="px-3 pt-2 pb-1 text-[11px] font-medium uppercase tracking-wider text-slate-500">
          Monthly emissions
        </div>
        <div className="px-3 pb-3">
          {series.length ? (
            <ResponsiveContainer width="100%" height={110}>
              <BarChart data={series} margin={{ top: 6, right: 6, bottom: 0, left: -8 }}>
                <CartesianGrid strokeDasharray="2 4" stroke="#e2e8f0" vertical={false} />
                <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#64748b' }} tickFormatter={monthLabel}
                  axisLine={false} tickLine={false} interval="preserveStartEnd" height={16} />
                <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} width={34}
                  tickFormatter={(v) => (v >= 1000 ? `${Math.round(v / 1000)}k` : `${v}`)} />
                <Tooltip cursor={{ fill: 'rgba(15,23,42,0.04)' }}
                  contentStyle={{ fontSize: 12, padding: '6px 10px', border: '1px solid #e2e8f0', borderRadius: 6 }}
                  labelFormatter={(m) => monthLabel(m)}
                  formatter={(v) => [`${fmt(v, 2)} t CO₂e`, 'Emissions']} />
                <Bar dataKey="co2e" fill="#10b981" radius={[3, 3, 0, 0]} maxBarSize={18} />
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex h-[110px] items-center justify-center text-xs text-slate-400">No usage data in the selected period</div>
          )}
        </div>
      </div>

      <div className="mt-1 flex items-center justify-around gap-2 rounded-md border border-slate-200/70 bg-slate-50/50 px-3 py-2 text-[11px]" data-testid="carbon-footer-stats">
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-base font-semibold tabular-nums text-slate-900">
            <Car className="h-3.5 w-3.5 text-emerald-600" />{fmt(eq.miles)}
          </div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Miles driven</div>
        </div>
        <div className="h-7 w-px bg-slate-200" aria-hidden />
        <div className="text-center">
          <div className="flex items-center justify-center gap-1 text-base font-semibold tabular-nums text-slate-900">
            <Trees className="h-3.5 w-3.5 text-emerald-600" />{fmt(eq.seedlings)}
          </div>
          <div className="text-[10px] uppercase tracking-wide text-muted-foreground">Tree seedlings · 10 yr</div>
        </div>
      </div>
    </div>
  );
}

export default SustainabilityCard;
